// src/components/open-houses/checkin/KioskGuestFlow.jsx
// The guest-facing screens. One question per screen, giant tap targets.
// Owns the guest draft; calls onComplete(draft) when the flow finishes and
// resets itself. Activity is tracked centrally on the wrapper (idle timer).
import { useEffect, useRef, useState } from "react";
import {
  buildKioskScreens, newGuestDraft, canLeaveName, canLeaveContact,
  CORE_INTENTS, PARTY_CHOICES, formatPhoneAsTyped,
} from "../../../utils/checkinFlow";

const GOLD = "#d4af6a";
const IDLE_RESET_MS = 90 * 1000;
const THANKS_RESET_MS = 6 * 1000;

export default function KioskGuestFlow({ openHouse, template, onComplete }) {
  const screens = buildKioskScreens(template);
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState(newGuestDraft());
  const [showAgentInfo, setShowAgentInfo] = useState(false);
  const idleTimer = useRef(null);

  const screen = screens[idx];
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const reset = () => { setDraft(newGuestDraft()); setShowAgentInfo(false); setIdx(0); };

  // Idle reset: a wandered-off guest mid-flow should not leave their info
  // on screen. Never fires on the welcome screen.
  const poke = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (idx > 0 && screen.type !== "thanks") {
      idleTimer.current = setTimeout(reset, IDLE_RESET_MS);
    }
  };
  useEffect(() => { poke(); return () => clearTimeout(idleTimer.current); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Thanks screen: hand the completed draft up exactly once, then reset.
  useEffect(() => {
    if (screen.type !== "thanks") return;
    onComplete(draft);
    const t = setTimeout(reset, THANKS_RESET_MS);
    return () => clearTimeout(t);
  }, [screen.type]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => { setShowAgentInfo(false); setIdx((i) => Math.min(i + 1, screens.length - 1)); };

  const stepLabel = idx > 0 && screen.type !== "thanks"
    ? `Question ${idx} of ${screens.length - 2}` : "";

  // NOTE: Btn and KioskInput live at module scope (bottom of this file).
  // Defining them here would give React a new component type every render,
  // remounting the text inputs on each keystroke and dropping the iPad
  // keyboard focus. Activity tracking is centralized on the wrapper div
  // (onPointerDown + onInputCapture), so they don't need `poke` at all.

  const first = draft.firstName.trim();
  const addr = openHouse.propertyAddress?.split(",")[0] || "our open house";

  return (
    <div className="w-full max-w-xl mx-auto text-white" onPointerDown={poke} onInputCapture={poke}>
      {stepLabel && <div className="text-xs uppercase tracking-widest text-white/60 mb-3">{stepLabel}</div>}

      {screen.type === "welcome" && (
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#d4af6a] mb-2">Welcome to</div>
          <h1 className="font-serif text-5xl mb-2">{addr}</h1>
          <p className="text-white/80 mb-10">
            Hosted by {openHouse.hostingAgentName || "Team Edelstein"} · Team Edelstein
          </p>
          <Btn onClick={next}>Tap to sign in</Btn>
        </div>
      )}

      {screen.type === "name" && (
        <div>
          <h2 className="font-serif text-4xl mb-6">Welcome in! What's your name?</h2>
          <div className="space-y-3 mb-6">
            <KioskInput placeholder="First name" value={draft.firstName} onChange={(e) => set({ firstName: e.target.value })} />
            <KioskInput placeholder="Last name" value={draft.lastName} onChange={(e) => set({ lastName: e.target.value })} />
          </div>
          <Btn onClick={next} disabled={!canLeaveName(draft)}>Next</Btn>
        </div>
      )}

      {screen.type === "contact" && (
        <div>
          <h2 className="font-serif text-4xl mb-2">Best way to reach you{first ? `, ${first}` : ""}?</h2>
          <p className="text-white/70 mb-6">Phone or email, whichever you prefer.</p>
          <div className="space-y-3 mb-6">
            <KioskInput placeholder="Phone" inputMode="tel" value={draft.phone} onChange={(e) => set({ phone: formatPhoneAsTyped(e.target.value) })} />
            <KioskInput placeholder="Email" inputMode="email" value={draft.email} onChange={(e) => set({ email: e.target.value })} />
          </div>
          <Btn onClick={next} disabled={!canLeaveContact(draft)}>Next</Btn>
        </div>
      )}

      {screen.type === "agent" && !showAgentInfo && (
        <div>
          <h2 className="font-serif text-4xl mb-8">Are you working with an agent?</h2>
          <div className="flex gap-4">
            <Btn onClick={() => { set({ hasAgent: true }); setShowAgentInfo(true); }}>Yes</Btn>
            <Btn ghost onClick={() => { set({ hasAgent: false, agentName: "", agentBrokerage: "", agentPhone: "" }); next(); }}>No</Btn>
          </div>
        </div>
      )}

      {screen.type === "agent" && showAgentInfo && (
        <div>
          <h2 className="font-serif text-4xl mb-2">Great! Who are you working with?</h2>
          <p className="text-white/70 mb-6">Just the basics, so we can say hello.</p>
          <div className="space-y-3 mb-6">
            <KioskInput placeholder="Agent's name" value={draft.agentName} onChange={(e) => set({ agentName: e.target.value })} />
            <KioskInput placeholder="Brokerage" value={draft.agentBrokerage} onChange={(e) => set({ agentBrokerage: e.target.value })} />
            <KioskInput placeholder="Agent's phone (optional)" inputMode="tel" value={draft.agentPhone} onChange={(e) => set({ agentPhone: formatPhoneAsTyped(e.target.value) })} />
          </div>
          <Btn onClick={next}>Next</Btn>
        </div>
      )}

      {screen.type === "intent" && (
        <div>
          <h2 className="font-serif text-4xl mb-8">What brings you in today?</h2>
          <div className="flex flex-col gap-3">
            {CORE_INTENTS.map((o) => (
              <Btn key={o.value} ghost onClick={() => { set({ intent: o.value }); next(); }}>
                {o.label}
              </Btn>
            ))}
          </div>
        </div>
      )}

      {screen.type === "party" && (
        <div>
          <h2 className="font-serif text-4xl mb-8">How many are in your group today?</h2>
          <div className="flex flex-wrap gap-3">
            {PARTY_CHOICES.map((nq) => (
              <Btn key={nq} ghost onClick={() => { set({ partySize: nq }); next(); }}>{nq}{nq === PARTY_CHOICES[PARTY_CHOICES.length - 1] ? "+" : ""}</Btn>
            ))}
          </div>
        </div>
      )}

      {screen.type === "custom" && (
        <CustomQuestion
          key={screen.key}
          question={screen.question}
          onAnswer={(val) => { set({ answers: { ...draft.answers, [screen.question.id]: val } }); next(); }}
          onSkip={() => next()}
        />
      )}

      {screen.type === "thanks" && (
        <div className="text-center">
          <h1 className="font-serif text-5xl mb-3">Enjoy the home{first ? `, ${first}` : ""}.</h1>
          <p className="text-white/80">We're glad you came by {addr}.</p>
        </div>
      )}
    </div>
  );
}

function CustomQuestion({ question, onAnswer, onSkip }) {
  const [text, setText] = useState("");
  return (
    <div>
      <h2 className="font-serif text-4xl mb-8">{question.prompt}</h2>
      {question.type === "yesno" && (
        <div className="flex gap-4"><Btn onClick={() => onAnswer("Yes")}>Yes</Btn><Btn ghost onClick={() => onAnswer("No")}>No</Btn></div>
      )}
      {question.type === "choice" && (
        <div className="flex flex-col gap-3">
          {(question.options || []).map((o) => (
            <Btn key={o} ghost onClick={() => onAnswer(o)}>{o}</Btn>
          ))}
        </div>
      )}
      {question.type === "text" && (
        <div>
          <div className="mb-6"><KioskInput placeholder="Type here" value={text} onChange={(e) => setText(e.target.value)} /></div>
          <Btn onClick={() => onAnswer(text.trim())} disabled={!text.trim()}>Next</Btn>
        </div>
      )}
      {question.skippable !== false && (
        <button onClick={onSkip} className="block mt-6 text-white/50 text-sm underline">Skip</button>
      )}
    </div>
  );
}

// Module scope on purpose (see NOTE in KioskGuestFlow): stable component
// types keep React from remounting inputs mid-typing.
function Btn({ children, onClick, disabled, ghost }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full px-8 py-4 text-lg font-semibold transition ${
        ghost
          ? "border-2 border-white/60 text-white hover:border-white"
          : "text-[#1a2238] disabled:opacity-40"
      }`}
      style={ghost ? {} : { background: GOLD }}
    >
      {children}
    </button>
  );
}

function KioskInput(props) {
  return (
    <input
      {...props}
      className="w-full rounded-xl p-4 text-xl text-slate-900 outline-none"
    />
  );
}
